import { useState } from "react";
import { Award, Camera, X } from "lucide-react";
import { toast } from "sonner";
import { AWARD_REASONS, giveAward, uploadAwardPhoto } from "@/lib/rewards";
import { errorText } from "@/lib/family";
import { playChime } from "@/lib/chime";

export const GiveGoldenAcorn = ({ pin, profiles }) => {
  const [childId, setChildId] = useState("");
  const [reason, setReason] = useState("");
  const [custom, setCustom] = useState("");
  const [note, setNote] = useState("");
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const why = reason === "custom" ? custom.trim() : reason;

  const give = async () => {
    setBusy(true);
    try {
      const a = await giveAward(pin, { profile_id: childId, reason: why, note: note.trim() || null });
      if (photo) await uploadAwardPhoto(pin, a.id, photo).catch(() => toast.error("Golden acorn given, but the photo didn't upload. Try a smaller photo."));
      playChime(true);
      toast.success(`Golden acorn given to ${profiles.find((p) => p.id === childId)?.name}!`);
      setReason(""); setCustom(""); setNote(""); setPhoto(null);
    } catch (e) { toast.error(errorText(e)); }
    setBusy(false);
  };

  return (
    <section className="pp-card" data-testid="give-golden-acorn">
      <p className="pp-label">Parents only</p>
      <h2 className="pp-h2 flex items-center gap-2"><Award className="h-5 w-5 text-amber-500" /> Give a golden acorn</h2>
      <p className="opacity-75 mt-1">Noticed great manners in real life? Reward it. You know your child best.</p>
      {profiles.length === 0 ? <p className="pp-sub mt-4">Add a child above first.</p> : (
        <>
          <div className="pp-presets mt-4">
            {profiles.map((p) => (
              <button key={p.id} className={`pp-pill ${childId === p.id ? "on" : ""}`} onClick={() => setChildId(p.id)} data-testid={`award-child-${p.id}`}>{p.name}</button>
            ))}
          </div>
          <div className="pp-presets mt-4" data-testid="award-reasons">
            {[...AWARD_REASONS, "custom"].map((r, i) => (
              <button key={r} className={`pp-pill ${reason === r ? "on" : ""}`} onClick={() => setReason(r)} data-testid={`award-reason-${i}`}>
                {r === "custom" ? "Something else…" : r}
              </button>
            ))}
          </div>
          {reason === "custom" && (
            <input className="pp-input" maxLength={80} placeholder="What did they do?" value={custom} onChange={(e) => setCustom(e.target.value)} data-testid="award-custom-input" />
          )}
          <input className="pp-input" maxLength={200} placeholder="Add a proud note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="award-note-input" />
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <label className="pp-ghost cursor-pointer" data-testid="award-photo-label">
              <Camera className="h-4 w-4" /> {photo ? "Change photo" : "Add teacher note photo"}
              <input type="file" accept="image/jpeg,image/png,image/webp" capture="environment" className="hidden"
                onChange={(e) => setPhoto(e.target.files?.[0] || null)} data-testid="award-photo-input" />
            </label>
            {photo && (
              <span className="pp-photo-chip" data-testid="award-photo-preview">
                <img src={URL.createObjectURL(photo)} alt="Teacher note preview" />
                <button onClick={() => setPhoto(null)} aria-label="Remove photo" data-testid="award-photo-remove"><X className="h-3 w-3" /></button>
              </span>
            )}
          </div>
          <button className="pp-primary mt-4" disabled={busy || !childId || !why} onClick={give} data-testid="award-give-button">
            <Award className="h-5 w-5" /> Give golden acorn
          </button>
        </>
      )}
    </section>
  );
};
